import { useMemo } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { TrendingUp, TrendingDown, Minus, Info } from 'lucide-react';
import { cn } from '@/lib/utils';

interface ExecutionScoreProps {
  actionsLast7Days: number;
  contactedRate: number; // % of leads moved from new to contacted
  followupCompliance: number; // % of due followups completed
  pipelineMovements: number; // stage transitions
  staleLeadsCount: number;
  totalActiveLeads: number;
  previousScore?: number; // for delta comparison
}

interface ScoreLevel {
  label: string;
  color: string;
  bgColor: string;
}

function getScoreLevel(score: number): ScoreLevel {
  if (score >= 80) return { label: 'Elite', color: 'text-success', bgColor: 'bg-success/10' };
  if (score >= 60) return { label: 'Strong', color: 'text-primary', bgColor: 'bg-primary/10' };
  if (score >= 40) return { label: 'At Risk', color: 'text-warning', bgColor: 'bg-warning/10' };
  return { label: 'Needs Action', color: 'text-destructive', bgColor: 'bg-destructive/10' };
}

export function ExecutionScore({
  actionsLast7Days,
  contactedRate,
  followupCompliance,
  pipelineMovements,
  staleLeadsCount,
  totalActiveLeads,
  previousScore,
}: ExecutionScoreProps) {
  const score = useMemo(() => {
    // Weights for each signal
    const WEIGHTS = {
      actions: 25,
      contactedRate: 20,
      followupCompliance: 25,
      pipelineMovement: 20,
      stalePenalty: 10,
    };

    // Normalize actions (target: 20 actions/week = 100%)
    const actionsScore = Math.min(actionsLast7Days / 20, 1) * WEIGHTS.actions;

    // Contacted rate is already 0-100
    const contactedScore = (contactedRate / 100) * WEIGHTS.contactedRate;

    // Followup compliance is already 0-100
    const followupScore = (followupCompliance / 100) * WEIGHTS.followupCompliance;

    // Normalize pipeline movements (target: 10 movements/week = 100%)
    const movementScore = Math.min(pipelineMovements / 10, 1) * WEIGHTS.pipelineMovement;

    // Stale penalty: more stale leads = lower score
    const staleRatio = totalActiveLeads > 0 ? staleLeadsCount / totalActiveLeads : 0;
    const staleScore = (1 - staleRatio) * WEIGHTS.stalePenalty;

    return Math.round(actionsScore + contactedScore + followupScore + movementScore + staleScore);
  }, [actionsLast7Days, contactedRate, followupCompliance, pipelineMovements, staleLeadsCount, totalActiveLeads]);

  const hasEnoughData = actionsLast7Days > 0 || totalActiveLeads > 0;
  const scoreLevel = getScoreLevel(score);
  const delta = previousScore !== undefined ? score - previousScore : undefined;

  if (!hasEnoughData) {
    return (
      <Card className="border-dashed">
        <CardContent className="py-6">
          <div className="flex items-center justify-center gap-3 text-muted-foreground">
            <Info className="w-5 h-5" />
            <span className="text-sm">Not enough data yet. Take actions to build your Execution Score.</span>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className={cn('border-2', scoreLevel.bgColor)}>
      <CardContent className="py-5">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-3">
              <div className={cn(
                'w-14 h-14 rounded-xl flex items-center justify-center font-bold text-2xl',
                scoreLevel.bgColor,
                scoreLevel.color
              )}>
                {score}
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <h3 className={cn('font-semibold text-lg', scoreLevel.color)}>
                    {scoreLevel.label}
                  </h3>
                  {delta !== undefined && delta !== 0 && (
                    <span className={cn(
                      'flex items-center gap-0.5 text-xs font-medium',
                      delta > 0 ? 'text-success' : 'text-destructive'
                    )}>
                      {delta > 0 ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
                      {Math.abs(delta)}
                    </span>
                  )}
                  {delta === 0 && (
                    <span className="flex items-center gap-0.5 text-xs font-medium text-muted-foreground">
                      <Minus className="w-3 h-3" />
                      0
                    </span>
                  )}
                </div>
                <p className="text-xs text-muted-foreground">Execution Score (7d)</p>
              </div>
            </div>
          </div>

          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <button className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors px-2 py-1 rounded-md hover:bg-muted">
                  <Info className="w-3.5 h-3.5" />
                  How it's calculated
                </button>
              </TooltipTrigger>
              <TooltipContent side="bottom" className="max-w-xs">
                <div className="space-y-2 text-xs">
                  <p className="font-medium">Execution Score combines:</p>
                  <ul className="space-y-1 text-muted-foreground">
                    <li>• Actions taken (25%)</li>
                    <li>• Lead contact rate (20%)</li>
                    <li>• Follow-up compliance (25%)</li>
                    <li>• Pipeline movement (20%)</li>
                    <li>• Stale leads penalty (10%)</li>
                  </ul>
                </div>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
      </CardContent>
    </Card>
  );
}
